/**
 * Global realtime provider.
 *
 * Mounted once inside `<AuthGuard>` around `<AppLayout/>`. It:
 *   1. connects the singleton `wsClient`,
 *   2. subscribes to every realtime room,
 *   3. registers one `wsClient.on(event, handler)` listener per event a room
 *      can emit, and
 *   4. invalidates the room's RTK Query tags (always including `Dashboard`),
 *      coalescing bursts into a single refetch within a short trailing window.
 *
 * This is the single mechanism that keeps every page live without polling.
 * It must never throw on a missing room/event — unknown rooms simply resolve
 * to no tags.
 */

import { useEffect, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { wsClient } from '@/lib/ws';
import { apiSlice } from '@/store/api/apiSlice';
import {
  REALTIME_ROOMS,
  ROOM_EVENT_MAP,
  mapEventToTags,
  type TagType,
} from '@/lib/realtime';

/** Trailing debounce window that coalesces event bursts into one invalidation. */
const INVALIDATION_DEBOUNCE_MS = 250;

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();

  useEffect(() => {
    let pendingTags = new Set<TagType>();
    let flushTimer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      flushTimer = null;
      if (pendingTags.size === 0) return;
      const tags = Array.from(pendingTags);
      pendingTags = new Set<TagType>();
      dispatch(apiSlice.util.invalidateTags(tags));
    };

    const scheduleInvalidation = (tags: TagType[]) => {
      for (const tag of tags) pendingTags.add(tag);
      // Leading edge schedules the flush; subsequent events within the window
      // accumulate into the same batch (trailing window).
      if (flushTimer === null && pendingTags.size > 0) {
        flushTimer = setTimeout(flush, INVALIDATION_DEBOUNCE_MS);
      }
    };

    const disposers: Array<() => void> = [];

    // Connect (failure is non-fatal — persistence is the source of truth).
    wsClient.connect().catch(() => {});

    const subscribeRoom = (room: string) => {
      try {
        wsClient.subscribe(room).catch(() => {});
      } catch {
        // Never let a subscription failure break the provider.
      }
    };

    for (const room of REALTIME_ROOMS) {
      subscribeRoom(room);

      const eventTypes = ROOM_EVENT_MAP[room] ?? [];
      for (const eventType of eventTypes) {
        const off = wsClient.on(eventType, () => {
          scheduleInvalidation(mapEventToTags(room, eventType));
        });
        disposers.push(off);
      }
    }

    // Socket.io reconnection: re-subscribe every room so rooms are restored.
    const offConnected = wsClient.on('connected', () => {
      for (const room of REALTIME_ROOMS) subscribeRoom(room);
    });
    disposers.push(offConnected);

    return () => {
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      for (const off of disposers) {
        try {
          off();
        } catch {
          // Ignore listener-removal errors during teardown.
        }
      }
      for (const room of REALTIME_ROOMS) {
        try {
          wsClient.unsubscribe(room).catch(() => {});
        } catch {
          // Never throw while tearing down.
        }
      }
    };
  }, [dispatch]);

  return <>{children}</>;
}
